export const CODING_EVENT_KINDS = [
    "thinking",
    "plan_proposed",
    "plan_revised",
    "tool_call",
    "tool_result",
    "message",
    "awaiting_review",
    "review_posted",
    "completed",
    "error",
    "usage",
    "stage",
];
/**
 * The planning step's progress beats, in the order a planning run reaches
 * them. A planning run reads the repository and then plans for many minutes;
 * each beat is one plain sentence a person watching the live card can
 * follow. The ids are machine names for the frontend's lookup; the words in
 * {@link PLANNING_MILESTONE_COPY} are the only thing a person reads, and they
 * name no part of how the planner is built.
 */
export const PLANNING_MILESTONES = [
    // A later planning run on the same commit reuses the earlier run's reading
    // and records no reading of its own, so this is the first thing it says.
    "reading_reused",
    "drafting",
    "drafting_unread",
    "second_look",
    "angles",
    "checking",
    "writing",
    "last_read",
    "revising",
    "prototype",
    "prototype_attached",
    "plan_ready",
];
/**
 * `text` is the sentence the card and the conversation print. `label` is the
 * short phrase the card's sub-line counts time against while the step runs
 * ("Checking the plan · 1m 20s"); null for a beat that ends the work.
 */
export const PLANNING_MILESTONE_COPY = {
    reading_reused: { text: "Reusing the repository reading from an earlier run.", label: null },
    drafting: {
        text: "Drafting the plan from what the repository showed.",
        label: "Drafting the plan",
    },
    drafting_unread: { text: "Drafting the plan from the ticket.", label: "Drafting the plan" },
    second_look: {
        text: "Taking a second look at the repository.",
        label: "Reading the repository again",
    },
    angles: { text: "Weighing the work from a few angles.", label: "Weighing the angles" },
    checking: {
        text: "Checking the plan against the ticket and the code.",
        label: "Checking the plan",
    },
    writing: { text: "Writing up the final plan.", label: "Writing up the plan" },
    last_read: { text: "Giving the plan a last read.", label: "Last read of the plan" },
    revising: { text: "Revising the plan from that read.", label: "Revising the plan" },
    prototype: { text: "Sketching the screen this plan changes.", label: "Sketching the screen" },
    prototype_attached: { text: "Prototype attached to the plan.", label: null },
    plan_ready: { text: "Plan ready for your review.", label: null },
};
/** "Plan ready for your review.", or how many questions came with it. */
export function planReadyText(questions) {
    const count = Number.isFinite(questions) ? Math.max(0, Math.floor(questions)) : 0;
    if (count === 0)
        return PLANNING_MILESTONE_COPY.plan_ready.text;
    return `Plan ready, with ${count} question${count === 1 ? "" : "s"} for you.`;
}
export function isPlanningMilestone(value) {
    return typeof value === "string" && PLANNING_MILESTONES.includes(value);
}
/** The reused-reading sentence, with the earlier reading's file count when it has one. */
function readingReusedText(filesExamined) {
    const base = PLANNING_MILESTONE_COPY.reading_reused.text;
    if (typeof filesExamined !== "number" || !Number.isFinite(filesExamined) || filesExamined <= 0)
        return base;
    const count = Math.floor(filesExamined);
    return `${base.slice(0, -1)} (${count} file${count === 1 ? "" : "s"}).`;
}
/** One progress beat, as the event the live card already reads. */
export function planningMilestoneEvent(milestone, at, options = {}) {
    if (milestone === "reading_reused") {
        const text = readingReusedText(options.filesExamined);
        return options.reusedFromRunId
            ? {
                kind: "message",
                from: "sandbox",
                text,
                at,
                milestone,
                reusedFromRunId: options.reusedFromRunId,
            }
            : { kind: "message", from: "sandbox", text, at, milestone };
    }
    const text = milestone === "plan_ready"
        ? planReadyText(options.questions ?? 0)
        : PLANNING_MILESTONE_COPY[milestone].text;
    return { kind: "message", from: "sandbox", text, at, milestone };
}
/**
 * A build's setup, as the live card sees it. Since the toolchain install and
 * the sandbox snapshots, setup can run for many minutes before the build's
 * first turn, and the card read "Lining up. Nothing has been recorded yet" the
 * whole time (COREX-1291 on prod, 2026-10-12). The session posts one of these
 * each time setup moves on; the ids are machine names for the card's lookup
 * and the words in {@link BUILD_SETUP_STEP_COPY} are all a person reads.
 */
export const BUILD_SETUP_STEPS = [
    "sandbox",
    "clone",
    // A saved sandbox already holds the checkout; only the newest code is fetched.
    "fetch",
    // A fix round builds on the pull request's own branch.
    "pull_request",
    "install",
    // The saved sandbox's install was made on these same lockfiles and stands.
    "install_reused",
    "snapshot",
    "checks",
    "start",
    "ready",
];
/**
 * `text` is the sentence the card and the conversation print. `label` is the
 * short word the card's sub-line counts time against while the step runs
 * ("Install · 3m 12s"); null for the beat that ends setup.
 */
export const BUILD_SETUP_STEP_COPY = {
    sandbox: { text: "Starting the build sandbox.", label: "Sandbox" },
    clone: { text: "Cloning the repository.", label: "Clone" },
    fetch: { text: "Fetching the latest code into the saved sandbox.", label: "Fetch" },
    pull_request: { text: "Fetching the pull request.", label: "Pull request" },
    install: { text: "Installing the toolchain and dependencies.", label: "Install" },
    install_reused: {
        text: "Dependencies are already installed in the saved sandbox.",
        label: "Install (reused)",
    },
    snapshot: {
        text: "Saving the set-up sandbox so the next build starts faster.",
        label: "Saving the sandbox",
    },
    checks: {
        text: "Running the repository's checks on the unchanged code.",
        label: "Checks",
    },
    start: { text: "Handing the work to Wright.", label: "Start" },
    ready: { text: "Set up. Wright starts now.", label: null },
};
export function isBuildSetupStep(value) {
    return typeof value === "string" && BUILD_SETUP_STEPS.includes(value);
}
/** One setup beat, as the event the live card already reads. */
export function buildSetupEvent(setupStep, at) {
    return {
        kind: "message",
        from: "yagni",
        text: BUILD_SETUP_STEP_COPY[setupStep].text,
        at,
        setupStep,
    };
}
/** The short word the card's sub line counts a running check's time against. */
export const BUILD_CHECK_STEP_LABEL = "Checks";
const BUILD_CHECK_OUTCOME_WORDS = {
    passed: "passed",
    failed: "failed",
    failed_on_base: "failed, as it does on the unchanged code",
    no_verdict: "no verdict",
};
/** "12s", "3m 4s", "1h 5m". */
function checkDurationWords(ms) {
    const total = Math.max(0, Math.round(ms / 1000));
    if (total < 60)
        return `${total}s`;
    const minutes = Math.floor(total / 60);
    if (minutes < 60)
        return `${minutes}m ${total % 60}s`;
    return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}
/** The sentence a check's beat prints: "Running checks: pnpm test", "Checked pnpm test: passed in 2m 3s". */
export function buildCheckText(check) {
    const where = check.base ? " on the unchanged code" : "";
    if (check.phase === "start")
        return `Running checks${where}: ${check.command}`;
    const outcome = check.outcome ?? "no_verdict";
    const took = check.durationMs !== undefined && outcome !== "no_verdict"
        ? ` in ${checkDurationWords(check.durationMs)}`
        : "";
    return `Checked ${check.command}${where}: ${BUILD_CHECK_OUTCOME_WORDS[outcome]}${took}`;
}
/** One check's start or end, as the event the live card already reads. */
export function buildCheckEvent(check, at) {
    return { kind: "message", from: "yagni", text: buildCheckText(check), at, check };
}
/** The sentence a subagent's beat prints: "Subagent implementer: edit LiveStream.tsx". */
export function subagentBeatText(beat) {
    return beat.action
        ? `Subagent ${beat.agent}: ${beat.action}`
        : `Subagent ${beat.agent} is working`;
}
/** One subagent progress beat, as the event the live card already reads. */
export function subagentBeatEvent(beat, at) {
    return { kind: "message", from: "yagni", text: subagentBeatText(beat), at, subagent: beat };
}
/** The Live feed's row for a steering note the session received. */
export const STEERING_NOTE_HANDED_LINE = "Steering note handed to Wright";
/** The steering note's beat, as the event the live card already reads. */
export function steeringNoteEvent(at, stage) {
    return {
        kind: "message",
        from: "yagni",
        text: STEERING_NOTE_HANDED_LINE,
        at,
        steeringNote: { stage },
    };
}
/** Is this the beat that a steering note reached the session? */
export function isSteeringNoteEvent(event) {
    return event.kind === "message" && event.steeringNote != null;
}
//# sourceMappingURL=coding.js.map